import React from 'react';
import { ToastOptions, TypeOptions } from 'react-toastify';
import { showRibbonToast } from '../components/notifications/ribbon-toast';
import { RibbonToastSeverity } from '../components/notifications/ribbon-toast/ribbon-toast-severity';
import { BRAND_ASSETS_BASE } from '../constants';

function typeToSeverity(type: TypeOptions): RibbonToastSeverity {
  switch (type) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'info':
    case 'default':
    default:
      return 'info';
  }
}

/** Opens an in-app path or absolute http(s) URL from a toast action. */
function openToastLink(to: string): void {
  if (to.startsWith('http://') || to.startsWith('https://')) {
    window.open(to, '_blank', 'noopener,noreferrer');
    return;
  }
  window.location.assign(to);
}

/** True when `openToastLink` would open a new browser tab. */
function toastLinkOpensInNewTab(to: string): boolean {
  return to.startsWith('http://') || to.startsWith('https://');
}

interface NotifyConfig {
  readonly type: TypeOptions;
  readonly message: React.ReactNode;
  readonly icon?: React.ReactNode | false;
  readonly link?: {
    readonly text: string;
    readonly to: string;
  };
  readonly options?: ToastOptions;
}

type TypeConfig = React.ReactNode | Omit<NotifyConfig, 'type'>;

function messageIsNode(m: React.ReactNode | TypeConfig): m is React.ReactNode {
  return typeof (m as any).message === 'undefined';
}

function parseConfig(config: TypeConfig): Omit<NotifyConfig, 'type'> {
  if (messageIsNode(config)) {
    return {
      message: config,
    };
  }

  return config;
}

export const notifySuccess = (config: TypeConfig) => {
  notify({ type: 'success', ...parseConfig(config) });
};

export const notifyInfo = (config: TypeConfig) => {
  notify({ type: 'info', ...parseConfig(config) });
};

export const notifyWarn = (config: TypeConfig) => {
  notify({ type: 'warning', ...parseConfig(config) });
};

export const notifyError = (config: TypeConfig) => {
  notify({ type: 'error', ...parseConfig(config) });
};

const showBrowserNotification = (title: string, options?: NotificationOptions): void => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, options);
  }
};

/**
 * Shows an in-app ribbon toast (and optionally a browser notification for string messages).
 */
export const notify = (config: NotifyConfig) => {
  const { type, message: passedMessage, link, options } = config;
  const severity = typeToSeverity(type);

  if (typeof passedMessage === 'string') {
    const title = passedMessage.charAt(0).toUpperCase() + passedMessage.slice(1);
    Notification.requestPermission().then(() => {
      showBrowserNotification(title, {
        body: passedMessage,
        icon: `${BRAND_ASSETS_BASE}/favicon.ico`,
      });
    });
  }

  const autoClose =
    options?.autoClose === false
      ? false
      : typeof options?.autoClose === 'number'
        ? options.autoClose
        : undefined;

  try {
    showRibbonToast({
      severity,
      title: typeof passedMessage === 'string' ? passedMessage : undefined,
      children: typeof passedMessage === 'string' ? undefined : passedMessage,
      actionLabel: link?.text,
      onAction: link ? () => openToastLink(link.to) : undefined,
      opensInNewTab: link ? toastLinkOpensInNewTab(link.to) : false,
      autoClose,
      toastId: options?.toastId,
    });
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};

notify.success = notifySuccess;
notify.info = notifyInfo;
notify.warn = notifyWarn;
notify.error = notifyError;
